import { parseIrcLine, parsePrivmsg, ChatMessage, IrcLine } from 'lib/twitchChatParse'
import logger from 'logger'

const log = logger.extend('twitch-chat')

const CHAT_WSS_URL = 'wss://irc-ws.chat.twitch.tv:443'
const JOIN_CHUNK_SIZE = 15
const JOIN_CHUNK_INTERVAL_MS = 11000
const MAX_BACKOFF_MS = 30000
const CLIENT_PING_INTERVAL_MS = 240000
const STALE_MS = 390000
const WATCHDOG_TICK_MS = 60000
const PONG_TIMEOUT_MS = 15000
const PING_MIN_INTERVAL_MS = 1000

export type ChatDisconnectReason = 'closed' | 'stale' | 'no-pong' | 'reconnect'

export interface ChatHandlers {
  onMessage: (message: ChatMessage) => void
  onMessageDeleted: (e: { channel: string, targetMsgId: string }) => void
  onChatCleared: (e: { channel: string, targetLogin: string | null }) => void
  onRoomId?: (e: { channel: string, roomId: string }) => void
  onConnected?: () => void
  onDisconnected?: (e: { reason: ChatDisconnectReason, lastInboundAt: number }) => void
}

export interface ChatConnection {
  close: () => void
  lastInboundAt: () => number
  ping: () => void
}

const randomFloat = (): number => {
  const buf = new Uint32Array(1)
  crypto.getRandomValues(buf)
  return buf[0] / 2 ** 32
}

const anonymousNick = (): string =>
  `justinfan${(Math.floor(randomFloat() * 80000) + 10000)}`

const normalizeChannel = (raw: string): string =>
  raw.trim().toLowerCase().replace(/^#/, '')

const isValidChannelLogin = (login: string): boolean =>
  /^[a-z0-9_]{1,25}$/.test(login)

const stripHash = (param: string | undefined): string =>
  (param ?? '').replace(/^#/, '')

export const connectTwitchChat = (channels: string[], handlers: ChatHandlers): ChatConnection => {
  const logins = [...new Set(channels.map(normalizeChannel))].filter(isValidChannelLogin)

  let ws: WebSocket | null = null
  let closed = false
  let attempt = 0
  let lastReceivedAt = 0
  let lastInboundAt = 0
  let inboundFrames = 0
  let pingSentAt = 0
  let framesAtPing = 0
  let welcomed = false
  let reconnectAsked = false
  let keepaliveTimers: Array<ReturnType<typeof globalThis.setInterval>> = []
  const timers = new Set<ReturnType<typeof globalThis.setTimeout>>()

  const clearKeepalive = (): void => {
    keepaliveTimers.forEach((t) => globalThis.clearInterval(t))
    keepaliveTimers = []
  }

  const later = (fn: () => void, ms: number): void => {
    const t = globalThis.setTimeout(() => {
      timers.delete(t)
      fn()
    }, ms)
    timers.add(t)
  }

  const retire = (socket: WebSocket, reason: ChatDisconnectReason): void => {
    if (closed || ws !== socket) return
    ws = null
    clearKeepalive()
    const finalInboundAt = lastInboundAt
    lastInboundAt = 0
    inboundFrames = 0
    pingSentAt = 0
    framesAtPing = 0
    welcomed = false
    reconnectAsked = false
    attempt += 1
    const delay = Math.min(1000 * 2 ** attempt, MAX_BACKOFF_MS) + Math.round(randomFloat() * 1000)
    log({ disconnected: true, reason, retryInMs: delay })
    later(connect, delay)
    if (reason !== 'closed') {
      try {
        socket.close()
      } catch {
      }
    }
    handlers.onDisconnected?.({ reason, lastInboundAt: finalInboundAt })
  }

  const sendPing = (socket: WebSocket): void => {
    if (ws !== socket || socket.readyState !== WebSocket.OPEN) return
    const framesBefore = inboundFrames
    pingSentAt = Date.now()
    framesAtPing = framesBefore
    socket.send('PING')
    later(() => {
      if (ws === socket && inboundFrames === framesBefore) retire(socket, 'no-pong')
    }, PONG_TIMEOUT_MS)
  }

  const startKeepalive = (socket: WebSocket): void => {
    clearKeepalive()
    lastReceivedAt = Date.now()
    keepaliveTimers = [
      globalThis.setInterval(() => sendPing(socket), CLIENT_PING_INTERVAL_MS),
      globalThis.setInterval(() => {
        if (ws !== socket) return
        const silentForMs = Date.now() - lastReceivedAt
        if (silentForMs > STALE_MS) {
          log({ staleConnection: true, silentForMs })
          retire(socket, 'stale')
        }
      }, WATCHDOG_TICK_MS)
    ]
  }

  const joinInChunks = (socket: WebSocket): void => {
    const chunkCount = Math.ceil(logins.length / JOIN_CHUNK_SIZE)
    Array.from({ length: chunkCount }, (_, i) =>
      logins.slice(i * JOIN_CHUNK_SIZE, (i + 1) * JOIN_CHUNK_SIZE)
    ).forEach((chunk, i) => {
      later(() => {
        if (socket.readyState !== WebSocket.OPEN) return
        socket.send(`JOIN ${chunk.map((c) => `#${c}`).join(',')}`)
      }, i * JOIN_CHUNK_INTERVAL_MS)
    })
  }

  const dispatch = (socket: WebSocket, line: IrcLine): void => {
    if (line.command === 'PING') {
      socket.send(`PONG :${line.params[0] ?? 'tmi.twitch.tv'}`)
      return
    }
    if (line.command === '001') {
      attempt = 0
      welcomed = true
      log({ connected: true, channels: logins.length })
      joinInChunks(socket)
      handlers.onConnected?.()
      return
    }
    if (line.command === 'PRIVMSG') {
      const message = parsePrivmsg(line)
      if (message !== null) handlers.onMessage(message)
      return
    }
    if (line.command === 'CLEARMSG') {
      const channel = stripHash(line.params[0])
      const targetMsgId = line.tags['target-msg-id'] ?? ''
      if (channel !== '' && targetMsgId !== '') handlers.onMessageDeleted({ channel, targetMsgId })
      return
    }
    if (line.command === 'CLEARCHAT') {
      const channel = stripHash(line.params[0])
      if (channel === '') return
      handlers.onChatCleared({ channel, targetLogin: line.params[1] ?? null })
      return
    }
    if (line.command === 'ROOMSTATE') {
      const channel = stripHash(line.params[0])
      const roomId = line.tags['room-id'] ?? ''
      if (channel !== '' && roomId !== '') handlers.onRoomId?.({ channel, roomId })
      return
    }
    if (line.command === 'RECONNECT') {
      log({ serverRequestedReconnect: true })
      reconnectAsked = true
    }
  }

  const connect = (): void => {
    if (closed) return
    const socket = new WebSocket(CHAT_WSS_URL)
    ws = socket

    socket.onopen = () => {
      socket.send('CAP REQ :twitch.tv/tags twitch.tv/commands')
      socket.send(`NICK ${anonymousNick()}`)
      startKeepalive(socket)
    }

    socket.onmessage = (e: MessageEvent) => {
      if (ws !== socket || typeof e.data !== 'string') return
      const receivedAt = Date.now()
      lastReceivedAt = receivedAt
      lastInboundAt = receivedAt
      inboundFrames += 1
      e.data.split('\r\n')
        .filter((raw) => raw !== '')
        .forEach((raw) => {
          if (closed || ws !== socket) return
          const line = parseIrcLine(raw)
          if (line !== null) dispatch(socket, line)
        })
      if (reconnectAsked) retire(socket, 'reconnect')
    }

    socket.onclose = () => retire(socket, 'closed')

    socket.onerror = () => {}
  }

  if (logins.length > 0) connect()

  return {
    close: () => {
      closed = true
      clearKeepalive()
      timers.forEach((t) => globalThis.clearTimeout(t))
      timers.clear()
      ws?.close()
    },
    lastInboundAt: () => lastInboundAt,
    ping: () => {
      const socket = ws
      if (closed || socket == null || !welcomed) return
      const now = Date.now()
      const waiting = pingSentAt > 0 && inboundFrames === framesAtPing
      if (waiting && now - pingSentAt < PONG_TIMEOUT_MS) return
      if (now - pingSentAt < PING_MIN_INTERVAL_MS) return
      sendPing(socket)
    }
  }
}

export const DELETE_SETTLE_MS = 2000
export const HELD_LINE_CAP_MS = 300000

export type HeldLineVerdict = 'release' | 'wait' | 'confirm' | 'drop'

export const heldTwitchLineVerdict = (p: {
  receivedAt: number
  delayMs: number
  now: number
  sameSocket: boolean
  lastInboundAt: number
}): HeldLineVerdict => {
  const confirmAfter = p.receivedAt + Math.max(p.delayMs, 0) + DELETE_SETTLE_MS
  if (p.lastInboundAt >= confirmAfter) return 'release'
  if (!p.sameSocket) return 'drop'
  if (p.now - p.receivedAt >= HELD_LINE_CAP_MS) return 'drop'
  return p.now >= confirmAfter ? 'confirm' : 'wait'
}

const REMEMBERED_SOCKETS = 256

export interface TwitchLinkTracker {
  epoch: () => number
  attach: (connection: Pick<ChatConnection, 'lastInboundAt' | 'ping'>) => void
  detach: (connection: Pick<ChatConnection, 'lastInboundAt' | 'ping'>) => void
  ended: (lastInboundAt: number) => void
  verdict: (line: { receivedAt: number, epoch: number }, delayMs: number, now: number) => HeldLineVerdict
  ping: () => void
}

export const createTwitchLinkTracker = (): TwitchLinkTracker => {
  let epoch = 0
  let attached: Pick<ChatConnection, 'lastInboundAt' | 'ping'> | null = null
  const endedLastInboundAt = new Map<number, number>()
  const end = (lastInboundAt: number): void => {
    endedLastInboundAt.set(epoch, lastInboundAt)
    if (endedLastInboundAt.size > REMEMBERED_SOCKETS) {
      const oldest = endedLastInboundAt.keys().next().value
      if (oldest != null) endedLastInboundAt.delete(oldest)
    }
    epoch += 1
  }
  return {
    epoch: () => epoch,
    attach: (connection) => { attached = connection },
    detach: (connection) => {
      if (attached !== connection) return
      end(connection.lastInboundAt())
      attached = null
    },
    ended: end,
    verdict: (line, delayMs, now) => {
      const current = line.epoch === epoch
      return heldTwitchLineVerdict({
        receivedAt: line.receivedAt,
        delayMs,
        now,
        sameSocket: current && attached != null,
        lastInboundAt: current ? (attached?.lastInboundAt() ?? 0) : (endedLastInboundAt.get(line.epoch) ?? 0)
      })
    },
    ping: () => attached?.ping()
  }
}
